import { callApi } from '@/api/api.config';
import { buildPageSortFilterParams } from '@/api/api.params';
import { PaginatedResponse } from '@/api/api.types';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';

import {
  ChangePasswordSchema,
  ForgotPasswordSchema,
  GenerateResetTokenInputSchema,
  OtpSetupSchema,
  ResetTokenSchema,
  UserDeactivateSchema,
  UserManagementSchema,
} from './Users.schema';

const BASE = 'users';

export interface User {
  id: string;
  username: string;
  name: string;
  is_active: boolean;
  groups: string[];
  customers: string[];
  projects: string[];
  email: string;
}

async function forgotPassword(data: ForgotPasswordSchema) {
  return await callApi({
    method: 'POST',
    url: `/${BASE}/password/forgot/`,
    data,
  });
}

export const useForgotPassword = () =>
  useMutation({
    mutationFn: (data: ForgotPasswordSchema) => forgotPassword(data),
  });

interface ChangePasswordResponse {
  message: string;
  otp_status: boolean;
  otp_token: string;
}

async function changePassword(data: ChangePasswordSchema): Promise<ChangePasswordResponse> {
  return await callApi({
    method: 'POST',
    url: `/${BASE}/password/change/`,
    data,
  }).then((response) => response.data);
}

export const useChangePassword = () =>
  useMutation({
    mutationFn: (data: ChangePasswordSchema) => changePassword(data),
  });

async function checkResetToken(data: ResetTokenSchema) {
  return await callApi({
    method: 'POST',
    url: `/${BASE}/reset/validate/`,
    data,
  });
}

export const useCheckResetToken = () =>
  useMutation({
    mutationFn: (data: ResetTokenSchema) => checkResetToken(data),
  });

interface GenerateResetTokenResponse {
  token: string;
}

async function generateResetToken(data: GenerateResetTokenInputSchema): Promise<GenerateResetTokenResponse> {
  return await callApi({
    method: 'POST',
    url: `/${BASE}/reset/generate/`,
    data,
  }).then((response) => response.data);
}

export const useGenerateResetToken = () =>
  useMutation({
    mutationFn: (data: GenerateResetTokenInputSchema) => generateResetToken(data),
  });

export const useUserListData = (pagination: PaginationState, sorting: SortingState, columnFilters: ColumnFiltersState) => {
  return useQuery({
    queryKey: ['userList', pagination, sorting, columnFilters],
    queryFn: () => fetchUserListData(pagination, sorting, columnFilters),
    placeholderData: keepPreviousData,
  });
};

const fetchUserListData = async (
  pagination: PaginationState,
  sorting: SortingState,
  columnFilters: ColumnFiltersState
): Promise<PaginatedResponse<User>> => {
  return callApi({
    method: 'GET',
    url: `users/get_user_list/`,
    params: buildPageSortFilterParams({ pagination, sorting, columnFilters }),
  }).then((response) => response.data);
};

interface FetchedUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  user_type: string;
  client_associations: string[];
  client_cascades: string[];
  project_associations: string[];
  project_cascades: string[];
  farm_associations: string[];
  phone_number: string;
  country_code: string;
}

const fetchUser = async (userId?: string): Promise<FetchedUser> => {
  return callApi({ method: 'GET', url: `users/users/${userId}/` }).then((response) => response.data);
};

export const useFetchUser = (userId?: string) => {
  return useQuery({
    queryKey: ['fetchUser', userId],
    queryFn: () => fetchUser(userId),
    enabled: !!userId,
  });
};

interface ChangeUserParams {
  userId?: string;
  data: UserManagementSchema | UserDeactivateSchema;
}

const changeUser = async (editData: ChangeUserParams) => {
  const { userId, data } = editData;
  if (userId) {
    return await callApi({ method: 'PATCH', url: `users/users/${userId}/`, data: data }).then((response) => response.data);
  } else {
    return await callApi({ method: 'POST', url: `users/users/`, data: data }).then((response) => response.data);
  }
};

export const useChangeUser = () =>
  useMutation({
    mutationFn: (editData: ChangeUserParams) => changeUser(editData),
  });

interface UserFormOptions {
  user_types: string[];
  clients: string[];
  projects: { project_id: string; client_id: string }[];
  farms: { farm_id: string; project_id: string }[];
}

const fetchUserFormOptions = async (): Promise<UserFormOptions> => {
  return callApi({ method: 'GET', url: `users/form-options/` }).then((response) => response.data);
};

export const useFetchUserFormOptions = () => {
  return useQuery({
    queryKey: ['fetchUserFormOptions'],
    queryFn: () => fetchUserFormOptions(),
  });
};

async function otpSetup(data: OtpSetupSchema) {
  return await callApi({
    method: 'POST',
    url: `/${BASE}/otp/setup/`,
    data,
  }).then((response) => response.data);
}

export const useOtpSetup = () =>
  useMutation({
    mutationFn: (data: OtpSetupSchema) => otpSetup(data),
  });

export interface CountryCode {
  code: string;
  name: string;
  calling_code: string;
}

const fetchCountryCodes = async (): Promise<CountryCode[]> => {
  return callApi({ method: 'GET', url: `users/country-codes/` }).then((response) => response.data);
};

export const useFetchCountryCodes = () => {
  return useQuery({
    queryKey: ['countryCodes'],
    queryFn: fetchCountryCodes,
    staleTime: 24 * 60 * 60 * 1000,
  });
};
